import { FormattedMessage } from '@flipster/i18n';
import { hstack } from '@flipster/styled-system/patterns';
import { QuoteCurrencyListExceptUsdt } from '@flipster/utils/constants/currency';
import { mapToObj } from '@flipster/utils/map-to-obj';

import type { CarouselInfo } from './types';

export type MarketCarouselType =
  | 'tradfi'
  | 'tradfiAll'
  | 'tradfiPreIpo'
  | 'tradfiStocks'
  | 'tradfiEtf'
  | 'tradfiCommodity'
  | 'hotTradings'
  | 'newListings'
  | 'priceChange24h'
  | 'volume'
  | 'absFundingRates'
  | 'yourFrequentTrades'
  | (typeof QuoteCurrencyListExceptUsdt)[number];

export const tradFiCarouselIds = [
  'tradfiAll',
  'tradfiPreIpo',
  'tradfiStocks',
  'tradfiEtf',
  'tradfiCommodity',
] as const satisfies MarketCarouselType[];

const quoteCurrencyCarouselMapper: Record<(typeof QuoteCurrencyListExceptUsdt)[number], CarouselInfo> = mapToObj(
  QuoteCurrencyListExceptUsdt,
  (currency) => ({
    // 통화 코드를 그대로 제목으로 쓴다. 티커는 번역 대상이 아니고, 통화별 i18n 키에
    // 묶이지 않아야 큐레이션 목록에 통화를 추가하는 것만으로 동작한다.
    title: currency,
    seeAll: true,
    category: currency,
  }),
);

export const CarouselMapper: Record<MarketCarouselType, CarouselInfo> = {
  tradfi: {
    title: (
      <div className={hstack({ gap: 8 })}>
        <FormattedMessage id='Market.r260205.TradFi' />
      </div>
    ),
    seeAll: true,
    category: 'tradfi',
  },
  tradfiAll: {
    title: <FormattedMessage id='Autogen_common.r251126.s_All' />,
    seeAll: true,
    category: 'tradfi',
  },
  tradfiPreIpo: {
    title: <FormattedMessage id='Market.r260624.Preipo' />,
    seeAll: true,
    category: 'tradfi',
  },
  tradfiStocks: {
    title: <FormattedMessage id='Market.r260624.Stock' />,
    seeAll: true,
    category: 'tradfi',
  },
  tradfiEtf: {
    title: <FormattedMessage id='Market.r260624.Etf' />,
    seeAll: true,
    category: 'tradfi',
  },
  tradfiCommodity: {
    title: <FormattedMessage id='Market.r260624.Commodity' />,
    seeAll: true,
    category: 'tradfi',
  },
  hotTradings: {
    title: <FormattedMessage id='Trade.r260324.Hot' />,
    seeAll: true,
    category: 'hotTradings',
  },
  newListings: {
    title: <FormattedMessage id='Common.r250919.New' />,
    seeAll: true,
    category: 'newListings',
  },
  priceChange24h: {
    title: <FormattedMessage id='Market.r250919.Top_movers' />,
    seeAll: true,
    category: 'priceChange24h',
  },
  volume: {
    title: <FormattedMessage id='Market.r250919.High_volume' />,
    sub: <FormattedMessage id='Common.r250919.Hour_h_bracket' values={{ hour: 24 }} />,
    seeAll: true,
    category: 'volume',
    alertProps: {
      title: <FormattedMessage id='Market.r250919.High_volume' />,
      message: <FormattedMessage id='Market.r250925.High_volume_info' />,
      confirmText: <FormattedMessage id='Common.r250401.Ok' />,
    },
  },
  absFundingRates: {
    title: <FormattedMessage id='Home.r260708.Funding_rates' />,
    seeAll: true,
    category: 'absFundingRates',
    alertProps: {
      title: <FormattedMessage id='Market.r250919.High_funding_rates' />,
      message: <FormattedMessage id='Market.r250922.High_funding_rates_info' />,
      confirmText: <FormattedMessage id='Common.r250401.Ok' />,
    },
  },
  yourFrequentTrades: {
    title: <FormattedMessage id='Market.r250919.Your_frequent_trades' />,
    seeAll: true,
    category: 'yourFrequentTrades',
  },
  ...quoteCurrencyCarouselMapper,
};
